import { t } from "i18next";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { putRequest } from "lib/core/apiClient";
import { useItemEdit } from "./useItemEdit";
import { CartNoteAttributes, CartNoteConfigType, CartNoteType, saveNoteType } from "lib/types/cartNotes";
import { createPayload, getAppliedCartNote, getCartNoteType } from "lib/utils/cartnotes";
import { useState } from "react";
import { errorToast } from "lib/utils/toasters";

const defaultFreeNote: CartNoteConfigType = {
  enabled: false,
  icon: "EDIT",
  title: "",
  suggestionText: "",
};

const useCartNotes = () => {
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { checkoutId, availableAddOns, noteAttributes, appliedCartAddOns },
    actions: { updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();

  const { handleAddOnEdit, isLoading } = useItemEdit();
  const [isCartNoteTileLoading, setIsCartNoteTileLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showError, setShowError] = useState(false);

  const cartNoteAttributes: CartNoteAttributes = getCartNoteType(
    availableAddOns,
    merchant?.cartNote ?? defaultFreeNote,
  );

  const appliedCartNote = getAppliedCartNote(noteAttributes, appliedCartAddOns);
  const [inputValue, setInputValue] = useState(appliedCartNote.note);

  const handleSaveNote = async ({ inputValue, action }: saveNoteType) => {
    setIsCartNoteTileLoading(true);
    const payload = createPayload(inputValue, action, cartNoteAttributes);

    try {
      if (cartNoteAttributes.type === "FREE") {
        let response;
        if (isAuthenticated) {
          response = await putRequest(`/checkout/${checkoutId}`, payload, "KRATOS_PRIVATE");
          updateCheckoutBasedOnCheckoutResponse(response);
        } else {
          response = await putRequest(`/api/v1/checkout/${checkoutId}`, payload, "KRATOS");
          updateCheckoutBasedOnCheckoutResponse(response);
        }
        if (action === "DELETE") {
          setInputValue("");
        }
      } else if (cartNoteAttributes.type === "PAID") {
        if (action === "ADD" || action === "EDIT") {
          handleAddOnEdit({
            action: "ADD",
            addOnId: cartNoteAttributes?.cartNoteAddOnData?.addonId ?? "",
            addOnLevel: "CART",
            showFullScreenLoader: false,
            cartNotePayload: payload,
            isCartNote: true,
          });
        } else {
          handleAddOnEdit({
            action: "DELETE",
            addOnId: cartNoteAttributes?.cartNoteAddOnData?.addonId ?? "",
            addOnLevel: "CART",
            showFullScreenLoader: false,
            cartNotePayload: payload,
            isCartNote: true,
          });
          setInputValue("");
        }
      }
    } catch (error) {
      console.error("Error saving note:", error);
      errorToast(t("error_saving_note"))
    } finally {
      setIsCartNoteTileLoading(false);
      setIsEditMode(false);
    }
  };

  return {
    state: {
      cartNoteAttributes,
      isCartNoteTileLoading: isCartNoteTileLoading || isLoading,
      appliedCartNote,
      isEditMode,
      inputValue,
      showError,
    },
    actions: {
      handleSaveNote,
      setIsEditMode,
      setInputValue,
      setShowError,
    },
  };
};

export default useCartNotes;
